import { type ReactNode } from 'react'
import { styleFromString } from '@/msgdeskRuntime/legacyBridge/legacyActions'
import { dispatchSubsNavigation } from '@/msgdeskRuntime/subsNavigation'
import { useSharedSurface } from './SharedSurfaceContext'

// Reusable page leaves that still live in Shared: IconButton, MoreLink, and
// the SectionView mount region. The menu leaves (NavItem, ParentsMenu) moved
// into ../Menu next to the headless menu model; NavItem is re-exported below
// so existing import paths keep working.
//
// RefreshSubs, FoldSubsButton, and PagingBar have Legacy/Modern visual variants.
// Std selects Legacy visuals; mailbox selects Modern. CheckedsInfo shares one
// behavior component with an explicit visual mode.

export { NavItem } from '../Menu/NavItem'

function stopEvent(event: React.SyntheticEvent) {
  event.preventDefault()
  event.stopPropagation()
}

export function IconButton({
  id,
  className,
  title,
  style,
  onClick,
}: {
  id?: string
  className: string
  title?: string
  style?: string
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
}) {
  return (
    <span
      id={id}
      className={className}
      title={title}
      style={style ? styleFromString(style) : undefined}
      onClick={onClick}
    />
  )
}

export function MoreLink({ section, moreCount }: { section: 0 | 1; moreCount?: number }) {
  const { skin } = useSharedSurface()
  // BEHAVIOUR: ask legacy to load more; React renders the facade reaction.
  function handleMore(event: React.MouseEvent<HTMLElement>) {
    stopEvent(event)
    dispatchSubsNavigation({ action: 'load-more', section, parentElId: 0, target: event.currentTarget })
  }

  return (
    // With nothing left to load the link fully hides (display:none, no
    // reserved space) but stays MOUNTED — md-more-link is the legacy click
    // anchor and a stable address for chrome/smoke tooling. The (+N) count
    // mirrors the legacy renderer's _set_more_info label.
    <a
      href="#"
      className={`md-more-link ${skin.moreLink}`}
      style={typeof moreCount === 'number' && moreCount <= 0 ? { display: 'none' } : undefined}
      onClick={handleMore}
    >
      More{typeof moreCount === 'number' && moreCount > 0 ? `(+${moreCount})` : ''}{' '}
    </a>
  )
}

// Empty mount region that legacy fills (attachments / favs / trash / drafts / new msg form).
export function SectionView({
  id,
  className,
  style,
  children,
}: {
  id?: string
  className: string
  style?: string
  children?: ReactNode
}) {
  return (
    <div id={id} className={className} style={style ? styleFromString(style) : undefined}>
      {children}
    </div>
  )
}
